import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@crayon/design-system-react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  PriceRuleMigrationModel,
  PriceRuleStatus,
  ProgramType,
  Source,
} from "api/client.generated";
import { ValidationException } from "api/client.generated.extensions";
import FormAutocomplete, { FormAutocompleteOption } from "components/primitives/FormAutocomplete";
import FormCheckbox from "components/primitives/FormCheckbox";
import FormErrorMessage from "components/primitives/FormErrorMessage";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodyDateCell from "components/primitives/TableBodyDateCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableHeaderCell from "components/primitives/TableHeaderCell";
import useSourcePartnerPriceRules from "hooks/api/PartnerMigration/useSourcePartnerPriceRules";
import useApi from "hooks/api/useApi";
import useSourceCustomers from "hooks/api/useSourceCustomers";
import { ChecklistIcon } from "images/MuiIcons";
import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import NotificationMessage from "types/notification-message";
import { PriceRuleModelStringConstants } from "types/price-rule-model";
import TableColumnInfo from "types/table-column-info";
import getProgramTypeDisplayName from "utils/program-type";
import * as yup from "yup";
import MigratePartnerStepProps from "../MigratePartnerStepProps";
import MigrationFormActionButtons from "../MigrationFormActionButtons";

const PriceRulesStep = ({ onStepComplete }: MigratePartnerStepProps) => {
  const { priceRules, isPriceRulesFetching } = useSourcePartnerPriceRules();
  const [formErrorMsg, setFormErrorMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { partnersMigrationClient } = useApi();

  // migration to CIQ and support only one program at the moment
  const { customers, isCustomersFetching } = useSourceCustomers(
    Source.CloudIq,
    ProgramType.MicrosoftCsp,
  );

  const formSchema = yup.object().shape({
    items: yup.array().of(
      yup.object().shape({
        checked: yup.boolean(),
        id: yup.string().required(),
        ciqCustomer: yup
          .object()
          .shape({
            id: yup.string().required(), // ciqCustomerId
            label: yup.string().required(), // ciqCustomerName
          })
          .nullable()
          .when("checked", {
            is: (checked: boolean) => checked,
            then: (schema) => schema.required("Required"),
          }),

        // to display table fields

        action: yup.string(),
        rate: yup.string(),
        source: yup.string(),
        program: yup.string(),
        prismCustomer: yup.string(),
        product: yup.string(),
        startDate: yup.date(),
        endDate: yup.date(),
        modifiedDate: yup.date(),
      }),
    ),
  });

  type SchemaType = yup.InferType<typeof formSchema>;
  const { control, handleSubmit, clearErrors } = useForm<SchemaType>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items", keyName: "fieldId" });
  const fieldsWatch = useWatch({ control, name: "items" });

  useEffect(() => {
    if (!priceRules?.length) return;

    // if rerender, clean up fields to avoid duplication
    if (fields.length > 0) remove([...Array(fields.length).keys()]);

    priceRules
      .filter((x) => x.status !== PriceRuleStatus.Expired)
      .map((x) => x.toTableModel())
      .forEach((x) =>
        append({
          checked: false,
          id: x.id,
          ciqCustomer:
            x.customer === PriceRuleModelStringConstants.ALL_CUSTOMERS
              ? {
                  id: PriceRuleModelStringConstants.ALL_CUSTOMERS,
                  label: PriceRuleModelStringConstants.ALL_CUSTOMERS,
                }
              : null,

          action: x.action,
          rate: x.rateLabel,
          source: x.source,
          program: getProgramTypeDisplayName(x.program as ProgramType),
          prismCustomer: x.customer,
          product: x.product,
          startDate: x.startDate,
          endDate: x.endDate,
          modifiedDate: x.lastModifiedDate,
        }),
      );
  }, [priceRules, append, remove, fields.length]);

  // to clear validation errors when on item unselect action
  useEffect(() => {
    fieldsWatch
      ?.map((x, i) => ({ checked: x.checked, index: i }))
      .filter((x) => !x.checked)
      .forEach((x) => {
        clearErrors(`items.${x.index}.ciqCustomer`);
      });
  }, [fieldsWatch, clearErrors]);

  const columnOptions = useMemo<TableColumnInfo[]>(
    () => [
      { label: "" },
      { label: "Action" },
      { label: "Rate" },
      { label: "Source" },
      { label: "Program" },
      { label: "Prism Customer" },
      { label: "Cloud-iQ Customer", width: "20%" },
      { label: "Product" },
      { label: "Start Date" },
      { label: "End Date" },
      { label: "Modified Date" },
    ],
    [],
  );

  const customerSelectOptions = useMemo<FormAutocompleteOption[]>(() => {
    const options: FormAutocompleteOption[] =
      customers?.map((x) => ({ id: x.id, label: x.name }) as FormAutocompleteOption) ?? [];

    options.unshift({
      id: PriceRuleModelStringConstants.ALL_CUSTOMERS,
      label: PriceRuleModelStringConstants.ALL_CUSTOMERS,
    });
    return options;
  }, [customers]);

  const onSave = async (formData: SchemaType) => {
    try {
      setIsSaving(true);

      // prettier-ignore
      const migrations = formData.items
        ?.filter((x) => x.checked)
        .map((x) =>({
           id: x.id,
           sourceCustomerId: x.ciqCustomer?.id === PriceRuleModelStringConstants.ALL_CUSTOMERS
            ? undefined
            : x.ciqCustomer?.id,
          sourceCustomerName:
            x.ciqCustomer?.label === PriceRuleModelStringConstants.ALL_CUSTOMERS
            ? undefined
            : x.ciqCustomer?.label,
          }) as PriceRuleMigrationModel,
        );

      if (!migrations?.length) return;

      await partnersMigrationClient.migratePriceRules(migrations);

      onStepComplete();
    } catch (e: unknown) {
      const validationError = ValidationException.parse(e);
      if (validationError) {
        setFormErrorMsg(validationError.getFirstErrorMessage());
      } else {
        setFormErrorMsg(NotificationMessage.UNKNOWN_ERROR);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const DataTableHead = (
    <TableHead>
      <TableRow>
        <TableCell size="small" sx={{ width: "45px" }}>
          <Box display="flex">
            <ChecklistIcon />
          </Box>
        </TableCell>
        {columnOptions.slice(1).map((x, i) => (
          <TableHeaderCell key={x.label + String(i)} columnName={x.label} width={x.width} />
        ))}
      </TableRow>
    </TableHead>
  );

  const DataTableBody = (
    <TableBody>
      {fields.map((x, i) => (
        <TableRow key={x.fieldId}>
          <TableCell>
            <FormCheckbox
              control={control}
              bindSchemaFieldName={`items.${i}.checked`}
              label=""
              disabled={isSaving}
            />
          </TableCell>
          <TableBodyCell label={x.action} />
          <TableBodyCell label={x.rate} />
          <TableBodyCell label={x.source} />
          <TableBodyCell label={x.program} />
          <TableBodyCell label={x.prismCustomer} />
          {x.prismCustomer === PriceRuleModelStringConstants.ALL_CUSTOMERS ? (
            <TableBodyCell label={x.ciqCustomer?.label} />
          ) : (
            <TableCell>
              <Box display="flex">
                <FormAutocomplete
                  control={control}
                  bindSchemaFieldName={`items.${i}.ciqCustomer`}
                  label="Select customer"
                  disabled={!fieldsWatch?.at(i)?.checked || isSaving}
                  options={customerSelectOptions}
                  isLoading={isCustomersFetching}
                  sxRoot={{ flex: 1 }}
                  clearable
                  testId="ciq-customer-autocomplete"
                />
              </Box>
            </TableCell>
          )}
          <TableBodyCell label={x.product} />
          <TableBodyDateCell date={x.startDate} />
          <TableBodyDateCell date={x.endDate} />
          <TableBodyDateCell date={x.modifiedDate} />
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Box display="flex" flexDirection="column" gap={1}>
        <TableContainer component={Paper}>
          <Table testId="migrate-price-rules-table">
            {DataTableHead}
            {isPriceRulesFetching ? (
              <TableBodySkeleton colsCount={columnOptions.length} rowsCount={3} />
            ) : (
              DataTableBody
            )}
          </Table>
        </TableContainer>
        <FormErrorMessage message={formErrorMsg} />
        <MigrationFormActionButtons
          isSaveDisable={!fieldsWatch?.some((x) => x.checked)}
          onSkipClick={onStepComplete}
          isSaveLoading={isSaving}
        />
      </Box>
    </form>
  );
};

export default PriceRulesStep;
